/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { format } from "date-fns";
import { ArrowLeft, Eye, FileText } from "lucide-react";
import dataProvider from "../../authentication/dataProvider";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { UserData } from "../../types/users/user.view";
import CustomLoader from "../ui/custom-loader";

// Helper function to validate governmentId URL
const isValidGovernmentId = (govId: any): boolean => {
  if (!govId) return false;
  const str = String(govId).trim();
  if (str === "" || str === "null" || str === "undefined") return false;
  // Check if it's a valid URL
  return str.startsWith("http://") || str.startsWith("https://");
};

const UserViewActions = () => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center gap-4 mb-6">
      <Button variant="outline" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
      {/* <Button>
        <Edit className="h-4 w-4 mr-2" />
        Edit
      </Button> */}
    </div>
  );
};

const UserView = () => {
  const { id } = useParams();
  const [record, setRecord] = useState<UserData | null>(null);
  const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;

    // Fetch user data from API
    dataProvider
      .getOne("users", { id })
      .then((response) => {
        // Extract governmentId with multiple fallback checks
        const rawGovId =
          response.data?.governmentId ||
          response.data?.government_id ||
          (response.data as any)?.governmentId;

        // Process and clean governmentId value
        let processedGovId: string | undefined = undefined;

        if (rawGovId) {
          const govIdStr = String(rawGovId).trim();
          if (
            govIdStr !== "" &&
            govIdStr !== "null" &&
            govIdStr !== "undefined" &&
            govIdStr !== "null" &&
            govIdStr.length > 0
          ) {
            processedGovId = govIdStr;
          }
        }

        // Create processed data with explicit governmentId
        const processedData: UserData = {
          ...response.data,
          governmentId: processedGovId,
        };

        setRecord(processedData);
      })
      .catch((error) => {
        // Set record to null on error to show loading state
        setRecord(null);
      });
  }, [id]);

  if (!record)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <CustomLoader size="lg" />
      </div>
    );

  return (
    <div className="p-4">
      <UserViewActions />
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="profile">Profile</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <div className="space-y-6">
                <div className="flex flex-col items-center gap-4 py-6">
                  <Avatar className="h-32 w-32">
                    <AvatarImage src={record?.profile} alt={record?.name} />
                    <AvatarFallback>
                      {record?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium w-[15%]">ID</TableCell>
                      <TableCell className="w-[35%]">{record?.id}</TableCell>
                      <TableCell className="font-medium w-[15%]">
                        Name
                      </TableCell>
                      <TableCell className="w-[35%]">{record?.name}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium w-[15%]">
                        Email
                      </TableCell>
                      <TableCell className="w-[35%]">{record?.email}</TableCell>
                      <TableCell className="font-medium w-[15%]">
                        Role ID
                      </TableCell>
                      <TableCell className="w-[35%]">
                        {record?.role?.id}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium w-[15%]">
                        Role Name
                      </TableCell>
                      <TableCell className="w-[35%]">
                        {record?.role?.roleName}
                      </TableCell>
                      <TableCell className="font-medium w-[15%]">
                        Wallet Address
                      </TableCell>
                      <TableCell className="w-[35%] break-all">
                        {record?.walletAddress}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium w-[15%]">
                        Created At
                      </TableCell>
                      <TableCell className="w-[35%]">
                        {record?.createdAt &&
                          format(new Date(record.createdAt), "PPP")}
                      </TableCell>
                      <TableCell className="font-medium w-[15%]">
                        Active Status
                      </TableCell>
                      <TableCell className="w-[35%]">
                        <Badge
                          variant={record?.isActive ? "success" : "destructive"}
                        >
                          {record?.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium w-[15%]">
                        Admin Acceptance
                      </TableCell>
                      <TableCell className="w-[35%]">
                        <Badge
                          variant={
                            record?.acceptedByAdmin ? "success" : "destructive"
                          }
                        >
                          {record?.acceptedByAdmin
                            ? "Accepted"
                            : "Not Accepted"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium w-[15%]">
                        Status
                      </TableCell>
                      <TableCell className="w-[35%]">
                        {record?.status}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium w-[15%]">
                        Country
                      </TableCell>
                      <TableCell className="w-[35%]">
                        {record?.country || "N/A"}
                      </TableCell>
                      <TableCell className="font-medium w-[15%]">
                        Contact Number
                      </TableCell>
                      <TableCell className="w-[35%]">
                        {record?.contactNumber || "N/A"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium w-[15%]">
                        Government ID
                      </TableCell>
                      <TableCell className="w-[35%]">
                        <div className="flex items-center gap-2">
                          {record?.governmentId &&
                          isValidGovernmentId(record.governmentId) ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowDocumentModal(true)}
                                className="flex items-center gap-2"
                              >
                                <Eye className="h-4 w-4" />
                                View Document
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  window.open(
                                    String(record.governmentId),
                                    "_blank",
                                    "noopener,noreferrer",
                                  )
                                }
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Open Link
                              </Button>
                            </>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium w-[15%]" />
                      <TableCell className="w-[35%]" />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Government ID Document View Modal */}
      {record?.governmentId && (
        <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
          <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh] bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Government ID Document
              </DialogTitle>
              <DialogDescription>
                Review the user's government ID document
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 max-h-[70vh] overflow-auto">
              {record.governmentId ? (
                <div className="flex flex-col items-center gap-4">
                  {/* Check if it's a PDF by URL extension */}
                  {(() => {
                    const govIdUrl = record.governmentId || "";
                    const govIdLower = govIdUrl.toLowerCase();

                    // More comprehensive PDF detection
                    const isPdf =
                      govIdLower.endsWith(".pdf") ||
                      govIdLower.includes(".pdf?") ||
                      govIdLower.includes(".pdf#") ||
                      govIdLower.includes("/pdf/") ||
                      govIdLower.includes("application/pdf");

                    if (isPdf) {
                      return (
                        <div className="w-full">
                          <iframe
                            src={govIdUrl}
                            className="w-full h-[600px] border border-gray-300 rounded-lg"
                            title="Government ID Document"
                            allow="fullscreen"
                          />
                          <p className="text-sm text-gray-500 mt-2 text-center">
                            If the PDF doesn't load, click "Open in New Tab"
                          </p>
                        </div>
                      );
                    } else {
                      // Assume it's an image
                      return (
                        <div className="w-full flex justify-center">
                          <img
                            src={govIdUrl}
                            alt="Government ID"
                            className="max-w-full h-auto rounded-lg border border-gray-300 shadow-md"
                            style={{
                              maxHeight: "600px",
                            }}
                            onError={(e) => {
                              // If image fails to load, try displaying as PDF
                              const target = e.target as HTMLImageElement;
                              const container = target.parentElement;
                              if (container) {
                                target.style.display = "none";
                                const iframe = document.createElement("iframe");
                                iframe.src = govIdUrl;
                                iframe.className =
                                  "w-full h-[600px] border border-gray-300 rounded-lg";
                                iframe.title = "Government ID Document";
                                iframe.allow = "fullscreen";
                                container.appendChild(iframe);
                              }
                            }}
                          />
                        </div>
                      );
                    }
                  })()}
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() =>
                        window.open(
                          record.governmentId,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      Open in New Tab
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowDocumentModal(false)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No Government ID document available
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UserView;
